import { Card, Container, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import {
    SaveButton,
    SimpleForm,
    Toolbar,
    useDataProvider,
    useEditController,
    useNotify,
    usePermissions,
    useRecordContext,
    useRedirect,
} from 'react-admin';
import { SubmitHandler } from 'react-hook-form';
import { Navigate } from 'react-router';
import { CrmDataProvider } from '../providers/types';
import { Buyer, SalesFormData } from '../types';
import { BuyersInputs } from './BuyersInputs';

function EditToolbar() {
    return (
        <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <SaveButton />
        </Toolbar>
    );
}

export function BuyersEdit() {
    const { isPending, permissions } = usePermissions();

    const { record } = useEditController();

    const dataProvider = useDataProvider<CrmDataProvider>();
    const notify = useNotify();
    const redirect = useRedirect();

    const { mutate } = useMutation({
        mutationKey: ['signup'],
        mutationFn: async (data: SalesFormData) => {
            if (!record) {
                throw new Error('Record not found');
            }
            return dataProvider.update('buyers', {
                id: record.id,
                data: data,
                previousData: record,
            });
        },
        onSuccess: () => {
            redirect('/buyers');
        },
        onError: () => {
            notify('An error occurred. Please try again.');
        },
    });

    const onSubmit: SubmitHandler<SalesFormData> = async data => {
        mutate(data);
    };

    if (isPending) {
        return null;
    }

    if (permissions !== 'admin') {
        return <Navigate to="/" />;
    }

    return (
        <Container maxWidth="sm" sx={{ mt: 4 }}>
            <Card>
                <SimpleForm
                    toolbar={<EditToolbar />}
                    onSubmit={onSubmit as SubmitHandler<any>}
                    record={record}
                >
                    <BuyerEditTitle />
                    <BuyersInputs />
                </SimpleForm>
            </Card>
        </Container>
    );
}

const BuyerEditTitle = () => {
    const record = useRecordContext<Buyer>();
    if (!record) return null;
    return (
        <Typography variant="h6">
            Editar {record?.nome_razao} - {record?.caminho_canal}
        </Typography>
    );
};
